import { useState, useEffect, useCallback, useContext } from "react";
import { collection, query, where, getDocs, getCountFromServer, orderBy } from "firebase/firestore";
import { db } from "../../../../services/firebase";
import { TenantContext } from "../../../../contexts/TenantContext";

export const useActivityMembers = () => {
    const { idTenant } = useContext(TenantContext);
    
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState(null);

    // 1. Fetch activities and count for each
    const fetchActivitiesWithCount = useCallback(async () => {
        if (!idTenant) return;
        setLoading(true);

        try {
            // A. Buscar todas as atividades
            const actRef = collection(db, "tenants", idTenant, "activities");
            const actSnap = await getDocs(query(actRef, orderBy("order", "asc")));
            
            const fetchedActivities = [];
            actSnap.forEach(doc => {
                fetchedActivities.push({ id: doc.id, ...doc.data() });
            });

            // B. Para cada atividade, buscar a contagem de forma barata (getCountFromServer)
            const membersRef = collection(db, "tenants", idTenant, "members");
            
            const activitiesWithCount = await Promise.all(fetchedActivities.map(async (act) => {
                const countQuery = query(
                    membersRef, 
                    where("activityNames", "array-contains", act.name)
                );
                
                try {
                    const snap = await getCountFromServer(countQuery);
                    return { ...act, memberCount: snap.data().count };
                } catch(e) {
                    console.error(`Erro ao contar alunos da atividade ${act.name}:`, e);
                    return { ...act, memberCount: 0 };
                }
            }));

            // Ordena as atividades com mais alunos por padrão (opcional)
            // ou mantém o order das activities. Vamos manter o order nativo.
            setActivities(activitiesWithCount);
            
        } catch (error) {
            console.error("Erro ao carregar atividades e contagem:", error);
        } finally {
            setLoading(false);
        }
    }, [idTenant]);

    useEffect(() => {
        fetchActivitiesWithCount();
    }, [fetchActivitiesWithCount]);

    // 2. Fetch members real data when activity is clicked
    const handleFetchMembersForActivity = async (activity) => {
        if (!activity || !idTenant) return;
        
        setSelectedActivity(activity);
        setLoadingMembers(true);
        setSelectedMembers([]);

        try {
            const membersRef = collection(db, "tenants", idTenant, "members");
            const q = query(
                membersRef, 
                where("activityNames", "array-contains", activity.name)
            );
            
            const snap = await getDocs(q);
            const membersList = [];
            snap.forEach(doc => {
                const md = doc.data();
                // Opcional: Filtrar o status em memória para não precisar de índice composto no banco
                if (md.status === "active" || md.status === "suspended") {
                    membersList.push({ id: doc.id, ...md });
                }
            });
            
            // Order alfabetica na memoria p economizar indice composto
            membersList.sort((a,b) => (a.fullName || "").localeCompare(b.fullName || ""));
            setSelectedMembers(membersList);

        } catch (error) {
            console.error("Erro ao buscar alunos da atividade:", error);
        } finally {
            setLoadingMembers(false);
        }
    };

    return {
        activities,
        loading,
        selectedActivity,
        selectedMembers,
        loadingMembers,
        actions: {
            fetchActivitiesWithCount,
            handleFetchMembersForActivity,
            closeMembersList: () => setSelectedActivity(null)
        }
    };
};
